import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Input, Button, Typography, Space, Tooltip, Spin, message as antdMessage } from "antd";
import {
  InfoCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  ScissorOutlined,
  UndoOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import { CHEMDRAW } from "../../constants/labels";
import { Form } from "antd";

const { Text, Paragraph } = Typography;

const HISTORY_CAP = 20;
const CHEMDRAW_ATTACH_TIMEOUT_MS = 15000;
const CHEMDRAW_ATTACH_POLL_MS = 200;

const getChemDrawManager = () =>
  window.RevvitySignals?.ChemdrawWebManager ||
  window.RevvitySignals?.ChemdrawwebManager ||
  window.perkinelmer?.ChemdrawWebManager ||
  window.perkinelmer?.ChemdrawwebManager;

const getChemDrawConfig = () => ({
  properties: { chemservice: CHEMDRAW.CHEM_SERVICE_URL },
  appProperties: { chemservice: CHEMDRAW.CHEM_SERVICE_URL },
});

const firstUsableString = (...values) =>
  values.find((value) => typeof value === "string" && value.trim())?.trim() || "";

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getCdxml = (instance) => {
  if (typeof instance?.getCDXML !== "function") return "";
  try {
    return firstUsableString(instance.getCDXML());
  } catch {
    return "";
  }
};

const getSmilesFromCanvas = (instance) =>
  new Promise((resolve, reject) => {
    if (!instance) {
      reject(new Error("ChemDraw canvas is not ready."));
      return;
    }

    if (typeof instance.getSMILES === "function") {
      instance.getSMILES((smiles, error) => {
        if (error) reject(error instanceof Error ? error : new Error(String(error)));
        else resolve(firstUsableString(smiles));
      });
      return;
    }

    if (typeof instance.getProperties === "function") {
      instance.getProperties((props, error) => {
        if (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
          return;
        }
        try {
          const parsed = JSON.parse(props);
          resolve(firstUsableString(parsed.SMILES));
        } catch {
          reject(new Error("Error processing data from canvas."));
        }
      });
      return;
    }

    reject(new Error("ChemDraw SMILES API is not available."));
  });

const invokeCommand = (cmd) => {
  if (!cmd) return false;
  if (typeof cmd === "function") {
    try { cmd(); return true; } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ChemDraw] command callable threw:", e);
    }
  }
  for (const method of ["execute", "run", "invoke", "perform", "call", "exec"]) {
    if (typeof cmd[method] === "function") {
      try { cmd[method](); return true; } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(`[ChemDraw] command.${method}() threw:`, e);
      }
    }
  }
  if (cmd.command) return invokeCommand(cmd.command);
  return false;
};

const getCommand = (instance, name) => {
  if (typeof instance?.getCommandWithName !== "function" || !name) return null;
  try {
    return instance.getCommandWithName(name);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[ChemDraw] getCommandWithName("${name}") threw:`, error);
    return null;
  }
};

const findCommandName = (commandNames, patterns) => {
  if (!Array.isArray(commandNames)) return null;
  for (const pattern of patterns) {
    const match = commandNames.find((n) => pattern.test(n));
    if (match) return match;
  }
  return null;
};

const safeGetZoom = (instance) => {
  if (typeof instance?.getDocumentZoomLevel !== "function") return null;
  try { return instance.getDocumentZoomLevel(); } catch { return null; }
};

const safeSetZoom = (instance, zoom) => {
  if (zoom == null || typeof instance?.setDocumentZoomLevel !== "function") return;
  try { instance.setDocumentZoomLevel(zoom); } catch { /* ignore */ }
};

const syncCanvasSize = (instance, canvasId) => {
  if (typeof instance?.setCanvasSize !== "function") return;
  const root = document.getElementById(canvasId);
  if (!root) return;
  const { width, height } = root.getBoundingClientRect();
  if (!width || !height) return;
  try {
    instance.setCanvasSize(
      { width: Math.round(width), height: Math.round(height) },
      { doNotCropDrawings: true }
    );
  } catch {
    // Older ChemDraw builds may not support resizing options.
  }
};

const captureScrollPosition = (target) => {
  if (!target) return null;
  const windowLike = target.window === target;
  if (windowLike) {
    return { target, windowLike, left: target.scrollX || 0, top: target.scrollY || 0 };
  }
  if (typeof target.scrollLeft !== "number" || typeof target.scrollTop !== "number") return null;
  return { target, windowLike: false, left: target.scrollLeft, top: target.scrollTop };
};

const restoreScrollPosition = (entry) => {
  if (!entry?.target) return;
  try {
    if (entry.windowLike && typeof entry.target.scrollTo === "function") {
      entry.target.scrollTo(entry.left, entry.top);
      return;
    }
    entry.target.scrollLeft = entry.left;
    entry.target.scrollTop = entry.top;
  } catch {
    // ChemDraw can own cross-origin frames; ignore those.
  }
};

const captureCanvasViewport = (canvasId) => {
  const root = document.getElementById(canvasId);
  if (!root) return [];

  const targets = new Set([root]);
  root.querySelectorAll("*").forEach((node) => {
    if (node.scrollWidth > node.clientWidth || node.scrollHeight > node.clientHeight) {
      targets.add(node);
    }

    if (node.tagName?.toLowerCase() === "iframe") {
      try {
        targets.add(node.contentWindow);
        targets.add(node.contentWindow?.document?.documentElement);
        targets.add(node.contentWindow?.document?.body);
      } catch {
        // Cross-origin frames keep their own viewport state.
      }
    }
  });

  return Array.from(targets).map(captureScrollPosition).filter(Boolean);
};

const restoreCanvasViewport = (viewportState) => {
  viewportState.forEach(restoreScrollPosition);
};

const restoreCanvasViewportSoon = (viewportState) => {
  restoreCanvasViewport(viewportState);
  [0, 60, 180, 360].forEach((delay) => {
    window.setTimeout(() => restoreCanvasViewport(viewportState), delay);
  });
};

const parseCdxmlDocument = (cdxml) => {
  if (!cdxml || typeof window.DOMParser !== "function") return null;
  try {
    const doc = new window.DOMParser().parseFromString(cdxml, "application/xml");
    if (doc.getElementsByTagName("parsererror").length) return null;
    return doc;
  } catch {
    return null;
  }
};

const readPoint = (value) => {
  const [x, y] = String(value || "").trim().split(/\s+/).map(Number);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
};

const writePoint = (point) => `${point.x.toFixed(2)} ${point.y.toFixed(2)}`;

const median = (values) => {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

const getAtomPositions = (doc) => {
  const positions = new Map();
  doc.querySelectorAll("n[id][p]").forEach((node) => {
    const point = readPoint(node.getAttribute("p"));
    const id = node.getAttribute("id");
    if (id && point) positions.set(id, point);
  });
  return positions;
};

const shiftCdxmlPoints = (doc, dx, dy) => {
  doc.querySelectorAll("[p]").forEach((node) => {
    const point = readPoint(node.getAttribute("p"));
    if (!point) return;
    node.setAttribute("p", writePoint({ x: point.x + dx, y: point.y + dy }));
  });

  doc.querySelectorAll("[BoundingBox]").forEach((node) => {
    const values = String(node.getAttribute("BoundingBox") || "").trim().split(/\s+/).map(Number);
    if (values.length !== 4 || values.some((value) => !Number.isFinite(value))) return;
    node.setAttribute(
      "BoundingBox",
      [
        values[0] + dx,
        values[1] + dy,
        values[2] + dx,
        values[3] + dy,
      ].map((value) => value.toFixed(2)).join(" ")
    );
  });
};

const GEOMETRY_ATTRIBUTES = [
  "BoundingBox",
  "bounds",
  "Bounds",
  "Width",
  "Height",
  "WindowPosition",
  "WindowSize",
  "ViewBox",
  "Viewport",
  "Origin",
  "Scale",
  "Zoom",
];

const copyGeometryAttributes = (source, target) => {
  if (!source || !target) return false;
  let changed = false;
  GEOMETRY_ATTRIBUTES.forEach((name) => {
    if (!source.hasAttribute(name)) return;
    const value = source.getAttribute(name);
    if (target.getAttribute(name) === value) return;
    target.setAttribute(name, value);
    changed = true;
  });
  return changed;
};

const preserveOriginalPageGeometry = (originalDoc, finalDoc) => {
  let changed = copyGeometryAttributes(originalDoc.documentElement, finalDoc.documentElement);
  const originalPages = Array.from(originalDoc.getElementsByTagName("page"));
  const finalPages = Array.from(finalDoc.getElementsByTagName("page"));

  finalPages.forEach((page, index) => {
    const matchingPage =
      originalPages.find((candidate) => candidate.getAttribute("id") === page.getAttribute("id")) ||
      originalPages[index] ||
      originalPages[0];

    if (copyGeometryAttributes(matchingPage, page)) {
      changed = true;
    }
  });

  return changed;
};

const keepCdxmlAtOriginalPosition = (originalCdxml, finalCdxml) => {
  const originalDoc = parseCdxmlDocument(originalCdxml);
  const finalDoc = parseCdxmlDocument(finalCdxml);
  if (!originalDoc || !finalDoc) return finalCdxml;
  let changed = preserveOriginalPageGeometry(originalDoc, finalDoc);

  const originalPositions = getAtomPositions(originalDoc);
  const finalPositions = getAtomPositions(finalDoc);
  const deltas = [];

  finalPositions.forEach((point, id) => {
    const originalPoint = originalPositions.get(id);
    if (originalPoint) {
      deltas.push({ dx: originalPoint.x - point.x, dy: originalPoint.y - point.y });
    }
  });

  if (!deltas.length) {
    return changed ? new window.XMLSerializer().serializeToString(finalDoc) : finalCdxml;
  }

  const dx = median(deltas.map((delta) => delta.dx));
  const dy = median(deltas.map((delta) => delta.dy));
  if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
    return changed ? new window.XMLSerializer().serializeToString(finalDoc) : finalCdxml;
  }

  shiftCdxmlPoints(finalDoc, dx, dy);
  changed = true;
  return new window.XMLSerializer().serializeToString(finalDoc);
};

const isTextInputTarget = (target) => {
  const tagName = target?.tagName?.toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select";
};

const HISTORY_COMMAND_PATTERNS = {
  undo: [/^undo$/i, /edit.*undo/i, /undo/i],
  redo: [/^redo$/i, /edit.*redo/i, /redo/i],
};

export default function ChemDrawCanvas({
  canvasId,
  title,
  smilesValue,
  onChangeSmiles,
  helperText,
  isModalView = false,
  cdxmlToLoad,
  onCdxmlChange,
  canUndo: externalCanUndo,
  canRedo: externalCanRedo,
  onUndo: externalOnUndo,
  onRedo: externalOnRedo,
}) {
  const shortcutRootRef = useRef(null);
  const chemdrawInstanceRef = useRef(null);
  const isLoadingRef = useRef(false);
  const debounceRef = useRef(null);
  const onCdxmlChangeRef = useRef(onCdxmlChange);
  const cdxmlToLoadRef = useRef(cdxmlToLoad);
  const smilesValueRef = useRef(smilesValue);
  const skipNextSmilesLoadRef = useRef(null);
  const shortcutHandlerRef = useRef(null);
  const lastShortcutRef = useRef({ action: null, at: 0 });

  // ---- Custom undo/redo: stacks of CDXML snapshots ----
  // past[]   : states we can revert to via Undo  (most recent at end)
  // future[] : states we can re-apply via Redo   (most recent at end)
  const historyRef = useRef({ past: [], future: [] });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [attachStatus, setAttachStatus] = useState("loading");
  const [attachError, setAttachError] = useState("");
  const [attachAttempt, setAttachAttempt] = useState(0);

  const focusShortcutRoot = () => {
    window.setTimeout(() => {
      try {
        shortcutRootRef.current?.focus?.({ preventScroll: true });
      } catch {
        shortcutRootRef.current?.focus?.();
      }
    }, 0);
  };

  const refreshUndoRedoState = () => {
    setCanUndo(historyRef.current.past.length > 0);
    setCanRedo(historyRef.current.future.length > 0);
  };

  const clearHistory = () => {
    historyRef.current = { past: [], future: [] };
    refreshUndoRedoState();
  };

  const pushSnapshot = (cdxml) => {
    if (!cdxml) return;
    const { past } = historyRef.current;
    // Don't double-record if the latest snapshot is already this state.
    if (past.length && past[past.length - 1] === cdxml) return;
    past.push(cdxml);
    historyRef.current.future = [];
    if (past.length > HISTORY_CAP) past.shift();
    refreshUndoRedoState();
  };

  useEffect(() => { onCdxmlChangeRef.current = onCdxmlChange; }, [onCdxmlChange]);
  useEffect(() => { cdxmlToLoadRef.current = cdxmlToLoad; }, [cdxmlToLoad]);
  useEffect(() => { smilesValueRef.current = smilesValue; }, [smilesValue]);

  const updateSmilesWithoutCanvasReload = (smiles) => {
    const nextSmiles = smiles || "";
    skipNextSmilesLoadRef.current = nextSmiles;
    onChangeSmiles(nextSmiles);
  };

  const syncCanvasStateToParent = async (instance) => {
    const cdxml = getCdxml(instance);
    if (cdxml) onCdxmlChangeRef.current?.(cdxml);
    try {
      const smiles = await getSmilesFromCanvas(instance);
      updateSmilesWithoutCanvasReload(smiles || "");
    } catch {
      // The native command can leave the canvas empty; CDXML sync above is enough.
    }
  };

  const runNativeHistoryCommand = async (type) => {
    const instance = chemdrawInstanceRef.current;
    if (!instance) return false;

    let commandNames = [];
    try {
      commandNames = typeof instance.getAvailableCommandNames === "function"
        ? instance.getAvailableCommandNames() || []
        : [];
    } catch {
      commandNames = [];
    }

    const commandName = findCommandName(commandNames, HISTORY_COMMAND_PATTERNS[type] || []);
    if (!commandName) return false;

    const zoomBefore = safeGetZoom(instance);
    const cdxmlBefore = getCdxml(instance);
    if (!invokeCommand(getCommand(instance, commandName))) return false;
    await wait(180);
    safeSetZoom(instance, zoomBefore);

    const cdxmlAfter = getCdxml(instance);
    if (cdxmlBefore && cdxmlAfter && cdxmlBefore === cdxmlAfter) {
      return false;
    }

    await syncCanvasStateToParent(instance);
    return true;
  };

  const captureCdxmlChange = () => {
    if (isLoadingRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const instance = chemdrawInstanceRef.current;
      const cdxml = getCdxml(instance);
      if (cdxml) onCdxmlChangeRef.current?.(cdxml);
    }, 500);
  };

  const loadSmilesToCanvas = (instance, smiles) => {
    if (!instance || typeof instance.loadSMILES !== "function" || !smiles?.trim()) return;
    instance.loadSMILES(smiles.trim(), (cdxml, error) => {
      if (error) console.error(`Error loading SMILES into ${canvasId}:`, error);
      else if (cdxml) onCdxmlChangeRef.current?.(cdxml);
    });
  };

  // Restore a CDXML to the canvas without firing the change-callback chain
  // (so undo/redo don't pollute history with their own intermediate states).
  const loadCdxmlSilent = (instance, cdxml) => {
    if (!instance || !cdxml || typeof instance.loadCDXML !== "function") return false;
    isLoadingRef.current = true;
    try {
      instance.loadCDXML(cdxml);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[ChemDraw] silent loadCDXML failed:", error);
      isLoadingRef.current = false;
      return false;
    }
    setTimeout(() => { isLoadingRef.current = false; }, 200);
    return true;
  };

  const loadCdxmlAndCapture = (instance, cdxml) => {
    if (!cdxml || typeof instance?.loadCDXML !== "function") return;
    isLoadingRef.current = true;
    instance.loadCDXML(cdxml);
    setTimeout(() => {
      isLoadingRef.current = false;
      const nextCdxml = getCdxml(instance) || cdxml;
      onCdxmlChangeRef.current?.(nextCdxml);
    }, 150);
  };

  useEffect(() => {
    const manager = getChemDrawManager();
    if (!manager) {
      setAttachStatus("loading");
      const timer = setTimeout(() => {
        if (attachAttempt * CHEMDRAW_ATTACH_POLL_MS >= CHEMDRAW_ATTACH_TIMEOUT_MS) {
          console.error("ChemdrawWebManager not available for", canvasId);
          setAttachStatus("error");
          setAttachError("ChemDraw JS did not load. Please refresh the page or check the ChemDraw script URL.");
          return;
        }
        setAttachAttempt((prev) => prev + 1);
      }, CHEMDRAW_ATTACH_POLL_MS);
      return () => clearTimeout(timer);
    }

    manager.attach({
      id: canvasId,
      callback: (cd) => {
        chemdrawInstanceRef.current = cd;
        setAttachStatus("ready");
        setAttachError("");
        clearHistory(); // fresh canvas — fresh history
        if (typeof cd.setContentChangedHandler === "function") {
          cd.setContentChangedHandler(captureCdxmlChange);
        }
        syncCanvasSize(cd, canvasId);

        if (cdxmlToLoadRef.current && typeof cd.loadCDXML === "function") {
          loadCdxmlAndCapture(cd, cdxmlToLoadRef.current);
          return;
        }
        loadSmilesToCanvas(cd, smilesValueRef.current);
      },
      errorCallback: (error) => {
        console.error(`ChemDraw attach failed (${canvasId}):`, error);
        setAttachStatus("error");
        setAttachError(error?.message || "ChemDraw attach failed.");
      },
      licenseUrl: CHEMDRAW.LICENSE_URL,
      preservePageInfo: true,
      config: getChemDrawConfig(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasId, attachAttempt]);

  useEffect(() => {
    if (attachStatus !== "ready") return undefined;
    const onResize = () => syncCanvasSize(chemdrawInstanceRef.current, canvasId);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [attachStatus, canvasId]);

  useEffect(() => {
    const instance = chemdrawInstanceRef.current;
    if (!instance || !cdxmlToLoad) return;
    clearHistory();
    loadCdxmlAndCapture(instance, cdxmlToLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdxmlToLoad]);

  useEffect(() => {
    const instance = chemdrawInstanceRef.current;
    if (!instance || cdxmlToLoad || !smilesValue?.trim()) return;

    if (skipNextSmilesLoadRef.current !== null) {
      if (skipNextSmilesLoadRef.current === (smilesValue || "")) {
        skipNextSmilesLoadRef.current = null;
        return;
      }
      skipNextSmilesLoadRef.current = null;
    }

    loadSmilesToCanvas(instance, smilesValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smilesValue, cdxmlToLoad]);

  const internalLoadSmilesToCanvas = () => {
    const instance = chemdrawInstanceRef.current;
    if (!instance) {
      antdMessage.error("ChemDraw canvas is not ready.");
      return;
    }
    loadSmilesToCanvas(instance, smilesValue);
  };

  const internalGetSmilesFromCanvas = async () => {
    try {
      const smiles = await getSmilesFromCanvas(chemdrawInstanceRef.current);
      updateSmilesWithoutCanvasReload(smiles || "");
    } catch (error) {
      antdMessage.error(error.message || "Error getting SMILES from canvas.");
    }
  };

  const handleUndo = async () => {
    const instance = chemdrawInstanceRef.current;
    if (!instance) {
      antdMessage.error("ChemDraw canvas is not ready.");
      return;
    }

    const { past, future } = historyRef.current;
    if (past.length === 0) {
      if (externalCanUndo && typeof externalOnUndo === "function") {
        externalOnUndo();
        focusShortcutRoot();
        return;
      }
      antdMessage.info("Nothing to undo.");
      return;
    }

    const currentCdxml = getCdxml(instance);
    if (currentCdxml) future.push(currentCdxml);
    const previousCdxml = past.pop();
    refreshUndoRedoState();

    const zoomBefore = safeGetZoom(instance);
    if (loadCdxmlSilent(instance, previousCdxml)) {
      await wait(180);
      safeSetZoom(instance, zoomBefore);
      try {
        const smiles = await getSmilesFromCanvas(instance);
        updateSmilesWithoutCanvasReload(smiles || "");
      } catch { /* canvas may legitimately be empty */ }
      onCdxmlChangeRef.current?.(getCdxml(instance) || previousCdxml);
      focusShortcutRoot();
    }
  };

  const handleRedo = async () => {
    const instance = chemdrawInstanceRef.current;
    if (!instance) {
      antdMessage.error("ChemDraw canvas is not ready.");
      return;
    }

    const { past, future } = historyRef.current;
    if (future.length === 0) {
      if (externalCanRedo && typeof externalOnRedo === "function") {
        externalOnRedo();
        focusShortcutRoot();
        return;
      }
      antdMessage.info("Nothing to redo.");
      return;
    }

    const currentCdxml = getCdxml(instance);
    if (currentCdxml) past.push(currentCdxml);
    const nextCdxml = future.pop();
    refreshUndoRedoState();

    const zoomBefore = safeGetZoom(instance);
    if (loadCdxmlSilent(instance, nextCdxml)) {
      await wait(180);
      safeSetZoom(instance, zoomBefore);
      try {
        const smiles = await getSmilesFromCanvas(instance);
        updateSmilesWithoutCanvasReload(smiles || "");
      } catch { /* ignore */ }
      onCdxmlChangeRef.current?.(getCdxml(instance) || nextCdxml);
      focusShortcutRoot();
    }
  };

  const handleKeyboardShortcut = async (event) => {
    if (attachStatus !== "ready") return;
    if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
    if (event.type === "keydown" && event.repeat) return;
    if (isTextInputTarget(event.target)) return;

    const key = String(event.key || "").toLowerCase();
    const code = String(event.code || "").toLowerCase();
    const isZ = key === "z" || code === "keyz";
    const isY = key === "y" || code === "keyy";
    const isUndo = isZ && !event.shiftKey;
    const isRedo = isY || (isZ && event.shiftKey);
    if (!isUndo && !isRedo) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();
    event.nativeEvent?.stopImmediatePropagation?.();

    const action = isUndo ? "undo" : "redo";
    const now = Date.now();
    const lastShortcut = lastShortcutRef.current;
    const duplicateWindow = event.type === "keyup" ? 800 : 80;
    if (
      lastShortcut.action === action &&
      now - lastShortcut.at < duplicateWindow
    ) {
      return;
    }
    lastShortcutRef.current = { action, at: now };

    if (isUndo && historyRef.current.past.length > 0) {
      await handleUndo();
      return;
    }

    if (isRedo && historyRef.current.future.length > 0) {
      await handleRedo();
      return;
    }

    if (isUndo && externalCanUndo && typeof externalOnUndo === "function") {
      externalOnUndo();
      focusShortcutRoot();
      return;
    }

    if (isRedo && externalCanRedo && typeof externalOnRedo === "function") {
      externalOnRedo();
      focusShortcutRoot();
      return;
    }

    const nativeHandled = await runNativeHistoryCommand(action);
    if (nativeHandled) return;
    if (isUndo) handleUndo();
    else handleRedo();
  };

  shortcutHandlerRef.current = handleKeyboardShortcut;

  useEffect(() => {
    if (attachStatus !== "ready") return undefined;

    const listener = (event) => shortcutHandlerRef.current?.(event);
    const targets = new Set();
    const addTarget = (target) => {
      if (!target?.addEventListener || targets.has(target)) return;
      target.addEventListener("keydown", listener, true);
      target.addEventListener("keyup", listener, true);
      targets.add(target);
    };

    addTarget(window);
    addTarget(document);

    const addFrameTargets = (root = document.getElementById(canvasId)) => {
      const frames = root?.querySelectorAll?.("iframe") || [];
      frames.forEach((frame) => {
        try {
          addTarget(frame);
          addTarget(frame.contentWindow);
          addTarget(frame.contentDocument);
          addFrameTargets(frame.contentDocument);
        } catch {
          // Cross-origin ChemDraw frames will keep their native shortcuts.
        }
      });
    };

    addFrameTargets();
    const framePoll = window.setInterval(addFrameTargets, 500);

    return () => {
      window.clearInterval(framePoll);
      targets.forEach((target) => {
        target.removeEventListener("keydown", listener, true);
        target.removeEventListener("keyup", listener, true);
      });
    };
  }, [attachStatus, canvasId]);

  const handleExtractSelectedSmiles = async () => {
    const instance = chemdrawInstanceRef.current;
    if (!instance) {
      antdMessage.error("ChemDraw canvas is not ready.");
      return;
    }

    if (instance.selection && typeof instance.selection.isEmpty === "function") {
      try {
        if (instance.selection.isEmpty()) {
          antdMessage.warning("Select a structure on the canvas first.");
          return;
        }
      } catch { /* fall through */ }
    }

    let commandNames = [];
    try {
      if (typeof instance.getAvailableCommandNames === "function") {
        commandNames = instance.getAvailableCommandNames() || [];
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("[ChemDraw] getAvailableCommandNames threw:", error);
    }

    const invertName = findCommandName(commandNames, [
      /invert.*select/i,
      /select.*invert/i,
      /inverse.*select/i,
    ]);
    const deleteName = findCommandName(commandNames, [
      /^delete$/i,
      /^delete.*select/i,
      /^delete.*selected/i,
      /^remove.*select/i,
      /^remove.*selected/i,
    ]);
    const cutName = findCommandName(commandNames, [/^cut$/i, /^cut.*select/i]);
    const removeCommandNames = [deleteName, cutName].filter(Boolean);

    if (!invertName || !removeCommandNames.length) {
      antdMessage.error(
        "This ChemDraw build doesn't expose selection removal commands. Cannot extract."
      );
      return;
    }

    setExtracting(true);
    isLoadingRef.current = true;

    // Snapshot BEFORE — this is what Undo will return us to.
    const originalCdxml = getCdxml(instance);
    // Capture zoom so we can restore it after the cut, defeating any auto-fit.
    const zoomBefore = safeGetZoom(instance);
    const viewportBefore = captureCanvasViewport(canvasId);

    try {
      // eslint-disable-next-line no-console
      console.log(`[ChemDraw] cmd: ${invertName} -> ${removeCommandNames[0]}`);

      // 1) Invert the selection — now everything EXCEPT what the user wants is selected.
      if (!invokeCommand(getCommand(instance, invertName))) {
        antdMessage.error("Could not invert selection.");
        return;
      }
      await wait(80);

      // 2) Remove the now-selected "everything else" from the canvas.
      const removedSelection = removeCommandNames.some((name) => invokeCommand(getCommand(instance, name)));
      if (!removedSelection) {
        antdMessage.error("Could not remove selection. Restoring original.");
        // best-effort revert: re-invert so the original selection is restored.
        invokeCommand(getCommand(instance, invertName));
        return;
      }
      await wait(120);

      // 3) Restore the original zoom level so the viewport doesn't drift.
      safeSetZoom(instance, zoomBefore);
      restoreCanvasViewport(viewportBefore);
      await wait(40);
      restoreCanvasViewport(viewportBefore);

      // 4) Verify something is left on the canvas.
      let finalCdxml = getCdxml(instance);
      if (!finalCdxml) {
        if (originalCdxml) loadCdxmlSilent(instance, originalCdxml);
        antdMessage.error("Crop left the canvas empty. Restored original.");
        return;
      }

      const positionedCdxml = keepCdxmlAtOriginalPosition(originalCdxml, finalCdxml);
      if (positionedCdxml !== finalCdxml) {
        finalCdxml = positionedCdxml;
      }

      // 5) Push original onto undo history so the user can revert.
      if (originalCdxml) pushSnapshot(originalCdxml);

      // 6) Sync SMILES + parent state.
      let finalSmiles = "";
      try {
        finalSmiles = await getSmilesFromCanvas(instance);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[ChemDraw] getSMILES after crop threw:", error);
      }

      updateSmilesWithoutCanvasReload(finalSmiles || "");
      onCdxmlChangeRef.current?.(finalCdxml);
      restoreCanvasViewportSoon(viewportBefore);
      focusShortcutRoot();
      antdMessage.success("Selected structure extracted.");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[ChemDraw] extract failed:", error);
      if (originalCdxml) loadCdxmlSilent(instance, originalCdxml);
      antdMessage.error(error.message || "Could not extract selected structure.");
    } finally {
      isLoadingRef.current = false;
      setExtracting(false);
    }
  };

  const btnSize = "middle";

  return (
    <div
      ref={shortcutRootRef}
      tabIndex={-1}
      onKeyDownCapture={(event) => shortcutHandlerRef.current?.(event)}
      onKeyUpCapture={(event) => shortcutHandlerRef.current?.(event)}
      style={{ padding: isModalView ? "0" : "16px", border: isModalView ? "none" : "1px solid #f0f0f0", borderRadius: isModalView ? 0 : "6px" }}
    >
      {!isModalView && (
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Draw or modify your chemical structure below.<br />
          1) Click &lsquo;Load to Canvas&rsquo; to push the SMILES from the input field onto the canvas.<br />
          2) Click &lsquo;Get SMILES from Canvas&rsquo; to pull the drawn structure back into the input field.<br />
          3) After extracting, use the Undo / Redo buttons below to step through previous states.<br /><br />
          {helperText}
        </Paragraph>
      )}

      <div style={{ position: "relative", marginBottom: 10 }}>
        <div
          id={canvasId}
          style={{
            width: "100%",
            height: isModalView ? "calc(100vh - 150px)" : "min(72vh, 760px)",
            minHeight: isModalView ? "420px" : "360px",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            position: "relative",
          }}
        />
        {attachStatus === "loading" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.75)",
              zIndex: 2,
            }}
          >
            <Space>
              <Spin size="small" />
              <Text type="secondary">Loading ChemDraw...</Text>
            </Space>
          </div>
        )}
        {attachStatus === "error" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
              textAlign: "center",
              background: "#fff",
              zIndex: 2,
            }}
          >
            <Text type="danger">{attachError || "ChemDraw failed to load."}</Text>
          </div>
        )}
      </div>

      <Row gutter={6} align="middle" wrap={false} style={{ flexWrap: "nowrap" }}>
        <Col flex="none">
          <Tooltip title={(canUndo || externalCanUndo) ? "Undo" : "Nothing to undo"}>
            <Button
              icon={<UndoOutlined />}
              onClick={handleUndo}
              disabled={!(canUndo || externalCanUndo) || attachStatus !== "ready"}
              size={btnSize}
              aria-label="Undo"
            />
          </Tooltip>
        </Col>

        <Col flex="none">
          <Tooltip title={(canRedo || externalCanRedo) ? "Redo" : "Nothing to redo"}>
            <Button
              icon={<RedoOutlined />}
              onClick={handleRedo}
              disabled={!(canRedo || externalCanRedo) || attachStatus !== "ready"}
              size={btnSize}
              aria-label="Redo"
            />
          </Tooltip>
        </Col>

        <Col flex="none">
          <Tooltip title="Keep the selected structure, remove everything else, and update SMILES">
            <Button
              icon={extracting ? <Spin size="small" /> : <ScissorOutlined />}
              onClick={handleExtractSelectedSmiles}
              disabled={extracting || attachStatus !== "ready"}
              size={btnSize}
            >
              Extract Selected SMILES
            </Button>
          </Tooltip>
        </Col>

        <Col flex="auto" style={{ minWidth: 0 }}>
          <Form.Item
            label={
              isModalView ? null : (
                <Space>
                  <Text strong>{title || "SMILES Input"}</Text>
                  {helperText && (
                    <Tooltip title={helperText}>
                      <InfoCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
                    </Tooltip>
                  )}
                </Space>
              )
            }
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder={isModalView ? "SMILES" : `Enter or Edit SMILES for ${title}`}
              value={smilesValue || ""}
              onChange={(event) => onChangeSmiles(event.target.value)}
              allowClear
              size={btnSize}
            />
          </Form.Item>
        </Col>

        <Col flex="none">
          <Button
            icon={<UploadOutlined />}
            onClick={internalLoadSmilesToCanvas}
            disabled={!smilesValue?.trim() || attachStatus !== "ready"}
            size={btnSize}
          >
            {isModalView ? "Load" : "Load to Canvas"}
          </Button>
        </Col>

        <Col flex="none">
          <Button
            icon={<DownloadOutlined />}
            onClick={internalGetSmilesFromCanvas}
            type="primary"
            disabled={attachStatus !== "ready"}
            size={btnSize}
          >
            {isModalView ? "Get SMILES" : "Get SMILES from Canvas"}
          </Button>
        </Col>
      </Row>
    </div>
  );
}

ChemDrawCanvas.propTypes = {
  canvasId: PropTypes.string.isRequired,
  title: PropTypes.string,
  smilesValue: PropTypes.string,
  onChangeSmiles: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  isModalView: PropTypes.bool,
  cdxmlToLoad: PropTypes.string,
  onCdxmlChange: PropTypes.func,
  canUndo: PropTypes.bool,
  canRedo: PropTypes.bool,
  onUndo: PropTypes.func,
  onRedo: PropTypes.func,
};
